import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

interface UserAvatarProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * UserAvatar component that displays the user's profile picture with Google photo fallback
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  className,
  size = 'md'
}) => {
  const { user, userEmail, profilePictureUrl } = useAuth();
  const [imageError, setImageError] = useState(false);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-8 h-8 text-sm';
      case 'lg':
        return 'w-16 h-16 text-xl';
      default:
        return 'w-12 h-12 text-base';
    }
  };

  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    
    if (userEmail) {
      const nameParts = userEmail.split('@')[0].split('.');
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      }
      return nameParts[0][0].toUpperCase();
    }
    
    return '?';
  };

  // Handle image load errors by falling back to next option
  const handleImageError = () => {
    setImageError(true);
    // Clear invalid cached image
    if (userEmail) {
      const cachedUrl = localStorage.getItem(`profilePicture_${userEmail}`);
      if (cachedUrl && cachedUrl === imageUrl) {
        localStorage.removeItem(`profilePicture_${userEmail}`);
      }
    }
  };

  // Check localStorage for cached profile picture
  const cachedProfilePicture = userEmail ? localStorage.getItem(`profilePicture_${userEmail}`) : null;
  
  // Only update cache when AuthContext provides a NEW profile picture URL
  // (Don't clear cache just because AuthContext is null - it might still be loading)
  if (userEmail && profilePictureUrl && profilePictureUrl !== cachedProfilePicture) {
    localStorage.setItem(`profilePicture_${userEmail}`, profilePictureUrl);
  }

  // Determine which image to show with priority:
  // 1. Cached profile picture (immediate, prevents flash)
  // 2. AuthContext profile picture (when cache is empty)
  // 3. Google profile picture (from Firebase Auth)
  // 4. Fallback to initials
  let imageUrl = cachedProfilePicture || profilePictureUrl || user?.photoURL;
  
  // If there was an error loading the image, skip to next fallback
  if (imageError && imageUrl === cachedProfilePicture) {
    imageUrl = profilePictureUrl || user?.photoURL;
  }

  return (
    <div className={cn(
      'relative inline-flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold overflow-hidden',
      getSizeClasses(),
      className
    )}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Profile Picture"
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      ) : (
        <span className="select-none">
          {getInitials()}
        </span>
      )}
    </div>
  );
};

export default UserAvatar;
